import { Typography } from "@progress/kendo-react-common";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  getAllUsersM,
  getUserMRecentBookings,
} from "./services/userManagement.services";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import NoImage from "../../assets/Images/useravatar.jpg";
import _debounce from "lodash/debounce";
import {
  CompositeFilterDescriptor,
  filterBy,
} from "@progress/kendo-data-query";
import {
  GridCellProps,
  GridCustomCellProps,
  GridCustomHeaderCellProps,
  GridFilterChangeEvent,
  Grid as KendoGrid,
  GridColumn as Column,
  GridPageChangeEvent,
} from "@progress/kendo-react-grid";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import IconButton from "../../components/common/IconButton";
import { FiEdit } from "react-icons/fi";
import { LoadingPanel } from "../../components/layout/Loading";
import { setCurrentPage, setPageLimit } from "../pagination/paginationSlice";
import { setFilterWebsiteUsers } from "./userManagementSlice";
import { TextBox } from "@progress/kendo-react-inputs";
import { Button } from "@progress/kendo-react-buttons";
import { filterClearIcon } from "@progress/kendo-svg-icons";
import { formatIndianNumber } from "../../_helper/helper";
import { LuCalendarClock } from "react-icons/lu";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import AlertBox from "../../components/common/AlertBox";
import moment from "moment";
import { SuccessToast } from "../../components/toast/Toasts";

const UserManagement: React.FC = () => {
  const dispatch = useAppDispatch();
  useEffect(() => {
    return () => {
      dispatch(setCurrentPage(0));
    };
  }, []);
  useEffect(() => {
    return () => {
      dispatch(closeDialog());
    };
  }, []);

  return (
    <>
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 20px 20px 0",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Customer List
          </Typography.h4>
        </GridLayoutItem>
        <GridLayoutItem>
          <UserManageGridComponent />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

interface PageState {
  skip: number;
  take: number;
}

const initialDataState: PageState = { skip: 0, take: 10 };

const UserManageGridComponent: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const UserList = useAppSelector((state) => state.userM.UserMList);
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);
  const [page, setPage] = React.useState<PageState>(initialDataState);
  const loading = useAppSelector((state) => state.userM.loading);
  const FilterWebsiteUsers: any = useAppSelector(
    (state) => state.userM.FilterWebsiteUsers
  );
  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);
  const gridRef = useRef<any>(null);

  useEffect(() => {
    const payload = {
      ...FilterWebsiteUsers,
      limit: +pageLimit,
      skip: +currentPage * +pageLimit,
    };
    dispatch(getAllUsersM(payload));
  }, [currentPage, pageLimit]);

  const pageChange = (event: GridPageChangeEvent) => {
    const newPage = event.page.skip / event.page.take;
    dispatch(setPageLimit(event.page.take));
    dispatch(setCurrentPage(newPage));
  };

  const MyImageCommandCell = (props: GridCellProps) => {
    const Image = props.dataItem?.profile_photo;

    const handleImageClick = () => {
      if (Image && typeof Image === "string") {
        window.open(Image);
      }
    };

    if (!Image) {
      return (
        <td className="text-center">
          <img
            src={NoImage}
            alt="No Profile"
            style={{ width: "50px", height: "50px", borderRadius: "50%" }}
          />
        </td>
      );
    }
    return (
      <td className="text-center">
        <img
          src={Image}
          alt="Profile Photo"
          onClick={handleImageClick}
          style={{
            width: "50px",
            height: "50px",
            objectFit: "cover",
            borderRadius: "50%",
            cursor: "pointer",
          }}
        />
      </td>
    );
  };

  const MyEditCommandCell = (props: any) => {
    const handleEditItem = (user_guid: number) => {
      navigate("/usermanage/edit", {
        state: { ID: props.dataItem?.id, user_guid: user_guid },
      });
    };

    const handleOpenRecentBookingDialog = (user_guid: number) => {
      dispatch(getUserMRecentBookings({ user_guid }));
      dispatch(openDialog("recentBookingDialog"));
    };

    return (
      <td
        {...props.tdProps}
        className={`action-td ${props.className}`}
        style={{ ...props.tdProps.style }}
      >
        <IconButton
          type="button"
          fillMode="flat"
          size="small"
          title="Edit"
          onClick={() => handleEditItem(props.dataItem?.user_guid)}
        >
          <FiEdit className="absolute-position" style={{ fontSize: "20px" }} />
        </IconButton>
        <IconButton
          type="button"
          fillMode="flat"
          themeColor="dark"
          size="small"
          title="Recent Bookings"
          onClick={() =>
            handleOpenRecentBookingDialog(props.dataItem.user_guid)
          }
        >
          <LuCalendarClock
            className="absolute-position"
            style={{ fontSize: "20px" }}
          />
        </IconButton>
      </td>
    );
  };
  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
    setPage({ skip: 0, take: page.take });
  };

  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${props.index === 11 ? "locked-header" : ""}`}
    >
      {props.children}
    </HeaderThElement>
  );
  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };
  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <RecentBookingsDialog />
      {/* {UserList && UserList.length ? ( */}
      <KendoGrid
        style={{ maxHeight: "calc(100vh - 152px)" }}
        className="responsive-table"
        filterable={true}
        filter={filter}
        data={filter ? filterBy(UserList, filter) : UserList}
        onFilterChange={handleFilterChange}
        skip={currentPage * pageLimit}
        take={pageLimit}
        total={UserList[0]?.TotalRecord || UserList?.length}
        pageable={{
          buttonCount: 5,
          pageSizes: [10, 50, 100, 300, 500],
          pageSizeValue: pageLimit,
          type: "input",
        }}
        onPageChange={pageChange}
        cells={{
          headerCell: HeaderCustomCell,
          data: MyDataCustomCell,
        }}
      >
        <Column
          field="profile_photo"
          title="Profile"
          width={100}
          cell={MyImageCommandCell}
        />
        <Column
          field="first_name"
          title="First Name"
          width={120}
          filterCell={CommonFilterCell}
        />
        <Column
          field="last_name"
          title="Last Name"
          width={120}
          filterCell={CommonFilterCell}
        />
        <Column
          field="mobile_no"
          title="Mobile No"
          width={120}
          filterCell={CommonFilterCell}
        />
        <Column
          field="email_id"
          title="Email"
          filterCell={CommonFilterCell}
          width={300}
          cells={{
            data: (props) => (
              <td
                onClick={() => {
                  if (props.dataItem.email_id) {
                    navigator.clipboard.writeText(props.dataItem.email_id);
                    SuccessToast("Email Copied!");
                  }
                }}
                style={{ cursor: "pointer" }}
              >
                {props.dataItem.email_id || ""}
              </td>
            ),
          }}
        />
        <Column field="date_of_birth" title="D. O. B." width={100} />
        <Column field="city" title="City" width={120} />
        <Column
          field="TotalBookings"
          title="Total Bookings"
          width={125}
          cells={{
            data: (props) => (
              <td className="text-center fw-bold">
                {props.dataItem.TotalBookings || 0}
              </td>
            ),
          }}
        />
        <Column
          field="TotalBussiness"
          title="Total Bussiness"
          width={125}
          cells={{
            data: (props) => (
              <td className="text-end fw-bold">
                ₹{formatIndianNumber(props.dataItem.TotalBussiness || 0)}
              </td>
            ),
          }}
        />
        <Column
          field="WalletBalance"
          title="Wallet Balance"
          width={125}
          cells={{
            data: (props) => (
              <td className="text-end fw-bold">
                ₹{formatIndianNumber(props.dataItem.WalletBalance || 0)}
              </td>
            ),
          }}
        />
        <Column field="LastLogin" title="Last Login" width={160} />
        <Column
          field="id"
          title="Actions"
          cells={{ data: MyEditCommandCell }}
          width={"110px"}
          filterable={false}
          locked={true}
        />
      </KendoGrid>
    </>
  );
};

const CommonFilterCell = (props: any) => {
  const dispatch = useAppDispatch();
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);
  const FilterWebsiteUsers: any = useAppSelector(
    (state) => state.userM.FilterWebsiteUsers
  );

  const isFilterRef = useRef(true);
  const [inputValue, setInputValue] = useState(props.value || "");
  const [inputField, setInputField] = useState(props.field || "");

  const onChange = (event: any) => {
    setInputValue(event.target.value);
    setInputField(event.target.name);
  };

  const delayedApiCall = React.useCallback(
    _debounce((updatedFilters: any) => {
      dispatch(setCurrentPage(0));
      const payload = {
        ...updatedFilters,
        limit: +pageLimit,
        skip: 0 * +pageLimit,
      };

      dispatch(getAllUsersM(payload));
    }, 300),
    [dispatch]
  );

  useEffect(() => {
    if (!isFilterRef.current) {
      const updatedFilters = {
        ...FilterWebsiteUsers,
        [inputField]: inputValue,
      };
      dispatch(setFilterWebsiteUsers(updatedFilters));
      delayedApiCall(updatedFilters);
    } else {
      isFilterRef.current = false;
    }
    return () => {
      delayedApiCall.cancel();
    };
  }, [inputValue, delayedApiCall]);

  const onClearButtonClick = (event: any) => {
    event.preventDefault();
    props.onChange({
      value: null,
      operator: "",
      syntheticEvent: event,
    });

    const updatedFilters = { ...FilterWebsiteUsers, [props.field]: "" };
    dispatch(setFilterWebsiteUsers(updatedFilters));

    const payload = {
      ...updatedFilters,
      limit: +pageLimit,
      skip: +currentPage * +pageLimit,
    };

    dispatch(getAllUsersM(payload));
  };

  return (
    <div className="d-flex">
      <TextBox
        value={FilterWebsiteUsers[props.field] || ""}
        name={props.field || ""}
        onChange={onChange}
      />
      <Button
        svgIcon={filterClearIcon}
        title="Clear"
        disabled={!FilterWebsiteUsers[props.field]}
        onClick={onClearButtonClick}
        type="button"
        style={{
          marginLeft: "8px",
        }}
      ></Button>
    </div>
  );
};

const RecentBookingsDialog: React.FC = () => {
  const navigate = useNavigate();
  const gridRef = useRef<any>(null);
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const UserMRecentBookings = useAppSelector(
    (state) => state.userM.UserMRecentBookings
  );
  const loading = useAppSelector((state) => state.userM.loading);

  return (
    <>
      {dialogName === "recentBookingDialog" && (
        <AppDialog>
          <>
            <div className="d-flex justify-content-between ">
              <Typography.h5>{"Recent Bookings"}</Typography.h5>
              <DialogCloseButton isCrossSign={true} themeColor="error" />
            </div>
            <GridLayout style={{ position: "relative" }}>
              {loading && <LoadingPanel gridRef={gridRef} />}
              <GridLayoutItem
                style={{
                  maxHeight: "calc(100vh - 160px)",
                  minWidth: "900px",
                  overflow: "auto",
                  scrollbarWidth: "thin",
                  borderBottom: "1px solid lightgray",
                }}
              >
                {UserMRecentBookings && UserMRecentBookings?.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-bordered mb-0">
                      <thead>
                        <tr
                          style={{ whiteSpace: "nowrap" }}
                          className="text-center"
                        >
                          <th style={{ background: "black", color: "white" }}>
                            Date
                          </th>
                          <th
                            style={{
                              background: "black",
                              color: "white",
                              minWidth: 150,
                            }}
                          >
                            Booking No.
                          </th>
                          <th style={{ background: "black", color: "white" }}>
                            Sector
                          </th>
                          <th style={{ background: "black", color: "white" }}>
                            Total Pax
                          </th>
                          <th style={{ background: "black", color: "white" }}>
                            Total Amount
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {UserMRecentBookings?.map(
                          (item: any, index: number) => (
                            <Fragment key={index}>
                              <tr>
                                <td className="text-center">
                                  {moment(
                                    item?.onwardDetails?.departure_date_time,
                                    "MM/DD/YYYY HH:mm:ss"
                                  ).format("DD/MM/YYYY")}
                                </td>
                                <td rowSpan={item?.returnDetails ? 2 : 1}>
                                  <div
                                    className="hoverableName text-center"
                                    onClick={() =>
                                      navigate(
                                        `/flightbooking/view/${item?.portal_booking_no}`
                                      )
                                    }
                                  >
                                    {item?.portal_booking_no}
                                  </div>
                                </td>
                                <td>
                                  <div>
                                    {item?.onwardDetails?.departure_airport}
                                    {" - "}
                                    {item?.onwardDetails?.arrival_airport}
                                  </div>
                                </td>
                                <td className="fw-600 text-center">
                                  {item?.total_pax}
                                </td>
                                <td
                                  className="fw-600 text-end"
                                  rowSpan={item?.returnDetails ? 2 : 1}
                                >
                                  ₹{formatIndianNumber(item?.net_price)}
                                </td>
                              </tr>
                              {item?.returnDetails && (
                                <tr>
                                  <td className="text-center">
                                    {moment(
                                      item?.returnDetails?.departure_date_time,
                                      "MM/DD/YYYY HH:mm:ss"
                                    ).format("DD/MM/YYYY")}
                                  </td>
                                  <td>
                                    <div>
                                      {item?.returnDetails?.departure_airport}
                                      {" - "}
                                      {item?.returnDetails?.arrival_airport}
                                    </div>
                                  </td>
                                  <td className="fw-600 text-center">
                                    {item?.total_pax}
                                  </td>
                                </tr>
                              )}
                            </Fragment>
                          )
                        )}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <AlertBox />
                )}
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

export default UserManagement;
